import { CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { NO_CONNECTION_STATUS_CODE } from "~/src/api/types";
import { AlertVariant } from "~/src/components/Alerts/AlertDefault";
import { BottomBarDouble } from "~/src/components/BottomBars/BottomBars";
import SoSCard from "~/src/components/Card/SoSCard";
import { CloseIcon } from "~/src/components/Icons/Icons";
import Text from "~/src/components/Text/Text";
import { StorageContext } from "~/src/contexts/StorageContext/StorageProvider";
import { isApiError } from "~/src/helpers/isApiErrorHelper";
import { OmradeType } from "~/src/helpers/types";
import { useConfirmationDialog } from "~/src/hooks/useConfirmationDialog";
import { useStateful } from "~/src/hooks/useStateful";
import { ApiError, Favorittområde } from "../../api/types";
import { jaktdataApi } from "../../appGlobals";
import ButtonDefault, { ButtonVariant } from "../../components/Button/ButtonDefault";
import { ArtNames } from "../../components/Jaktomrade/ArtNames";
import AppDataContext from "../../contexts/AppDataContext/DataProvider";
import UIContext from "../../contexts/UIContext/UIProvider";
import TomtResultat from "../Oversikt/Components/TomtResultat";

const MittJaktomrade = (): JSX.Element => {
  const isDeleting = useStateful(false);
  const appData = useContext(AppDataContext);
  const ui = useContext(UIContext);
  const storage = useContext(StorageContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const jaktomrade: Favorittområde = location.state.jaktomrade;
  const jaktomradeAnsvarligType =
    jaktomrade.Områdetype === OmradeType.Jaktfelt
      ? t("pages.mittJaktomrade.jaktleder")
      : t("pages.mittJaktomrade.valdansvarlig");
  const jaktomradeNummer =
    jaktomrade.Områdetype === OmradeType.Jaktfelt
      ? t("pages.mittJaktomrade.jaktfeltnummer")
      : t("pages.mittJaktomrade.valdnummer");
  const omradekode = {
    Områdekode: jaktomrade.Områdekode || "",
    Områdetype: jaktomrade.Områdetype || "",
  };

  const handleUndo = async () => {
    if (!!jaktomrade.Områdekode && !!jaktomrade.Områdetype) {
      await jaktdataApi
        .postJaktområde({ Områdekode: jaktomrade.Områdekode, Områdetype: jaktomrade.Områdetype })
        .catch((e: ApiError) => {
          if (e.axiosError?.code === "ERR_NETWORK") {
            storage.add({
              objectType: "PendingJaktomrade",
              objectKey: omradekode,
              data: { ...jaktomrade, ...omradekode },
              isPending: true,
              operation: "create",
              lastUpdated: new Date(),
            });
          } else {
            throw e;
          }
        })
        .then(() => {
          appData.jaktområder.refetch();
        });
    }
  };

  const handleDelete = async () => {
    isDeleting.set(true);

    if (!jaktomrade?.Områdekode || !jaktomrade?.Områdetype) return;
    try {
      // slett lokal kopi (PendingJaktomrade) hvis den eksisterer
      storage.delete(
        { Områdekode: jaktomrade.Områdekode, Områdetype: jaktomrade.Områdetype },
        "PendingJaktomrade",
      );

      await jaktdataApi.deleteJaktområde(omradekode).then(async () => {
        await queryClient.invalidateQueries({ queryKey: ["jaktomrader"], exact: true });
        await appData.jaktområder.refetch();
      });

      ui.showSnackbar({
        message: t("jaktomrade.slettet"),
        undoFn: handleUndo,
        severity: AlertVariant.Success,
      });
    } catch (e) {
      if (
        isApiError(e) &&
        (e.axiosError?.code === "ERR_NETWORK" || e.statusCode === NO_CONNECTION_STATUS_CODE)
      ) {
        if (
          storage.get<Favorittområde>(
            { Områdekode: omradekode.Områdekode, OmradeType: omradekode.Områdetype },
            "PendingJaktomrade",
          )
        ) {
          storage.delete(
            { Områdekode: omradekode.Områdekode, OmradeType: omradekode.Områdetype },
            "PendingJaktomrade",
          );
          ui.showSnackbar({
            message: t("jaktomrade.slettet"),
            undoFn: handleUndo,
            severity: AlertVariant.Success,
          });
        } else {
          storage.add({
            objectType: "PendingJaktomrade",
            objectKey: omradekode,
            data: { ...jaktomrade, ...omradekode },
            isPending: true,
            operation: "delete",
            lastUpdated: new Date(),
          });
        }
        ui.showSnackbar({
          message: t("sync.noNetworkWillSyncLater") + e.message,
          severity: AlertVariant.Info,
        });
      } else {
        throw e;
      }
    } finally {
      appData.jaktområder.refetch().then(() => {
        isDeleting.set(false);
        navigate("/minside");
      });
    }
  };

  const confirmationDialog = useConfirmationDialog({
    title: t("actions.deleteFavorite"),
    onConfirm: async () => {
      await handleDelete();
    },
  });

  if (isDeleting.value) {
    return (
      <TomtResultat>
        <CircularProgress thickness={7} size={80} />
      </TomtResultat>
    );
  }

  if (!jaktomrade) {
    return <Text>{t("actions.notSelected")}</Text>;
  }

  return (
    <div className="h-full flex flex-col justify-between">
      <SoSCard
        className="m-4 mb-8"
        titleString={jaktomrade.Områdenavn ?? ""}
        titleIcon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
            role="graphics-symbol"
            aria-hidden
          >
            <path
              d="M0 24V5.64706L5.55556 9.88235L12.5 0L19.4444 5.64706H25V24H0ZM6.94444 19.7647L12.5 12L22.2222 19.6941V8.47059H18.4722L13.0556 4.05882L6.18056 13.8706L2.77778 11.2941V16.3765L6.94444 19.7647Z"
              fill="#024A5B"
            />
          </svg>
        }
        cardFields={[
          {
            label: jaktomradeNummer,
            value: jaktomrade.Områdekode,
          },
          {
            label: t("pages.mittJaktomrade.jaktPå"),
            value: ArtNames({ ...jaktomrade }),
          },
          {
            label: jaktomradeAnsvarligType,
            value: jaktomrade.AnsvarligPersonaliaNavn ?? "-",
          },
          {
            label: t("pages.mittJaktomrade.epostAdresse"),
            value: jaktomrade.AnsvarligPersonaliaEpost ?? "-",
          },
          {
            label: t("pages.mittJaktomrade.telefonnummer"),
            value: jaktomrade.AnsvarligPersonaliaTlf ?? "-",
          },
          {
            label: jaktomradeAnsvarligType,
            value: jaktomrade.AnsvarligPersonaliaNavn ?? "-",
          },

          jaktomrade.Områdetype === OmradeType.Jaktfelt
            ? {
                label: t("pages.mittJaktomrade.tilhørendeVald"),
                value: `${jaktomrade.TilhørerendeValdnavn} (${jaktomrade.TilhørendeValdkode})`,
              }
            : { value: "", label: "", show: false },

          jaktomrade.Områdetype === OmradeType.Jaktfelt || jaktomrade.Områdetype === OmradeType.Vald
            ? {
                label: t("pages.mittJaktomrade.tilhørendeKommune"),
                value: `${jaktomrade.TilhørerendeKommunenavn} (${jaktomrade.TilhørendeKommunekode})`,
              }
            : { label: "", value: "", show: false },

          jaktomrade.Områdetype === OmradeType.VillreinVald
            ? {
                label: t("pages.mittJaktomrade.tilhørendeVillreinomrade"),
                value: `${jaktomrade.TilhørendeVillreinområdenavn} (${jaktomrade.TilhørendeVillreinområdekode})`,
              }
            : { label: "", value: "", show: false },
        ]}
      >
        {confirmationDialog.element}
      </SoSCard>
      <BottomBarDouble
        buttonOne={
          <ButtonDefault
            id="slettFavorittButton"
            variant={ButtonVariant.Tertiary}
            label={t("actions.remove")}
            icon={
              <svg
                role="graphics-symbol"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="3"
                viewBox="0 0 20 3"
                fill="currentColor"
              >
                <path d="M0 3V0H20V3H0Z" />
              </svg>
            }
            onClick={() => {
              confirmationDialog.show({ message: t("pages.mittJaktomrade.confirmDelete") });
            }}
          />
        }
        buttonTwo={
          <ButtonDefault
            id="lukkButton"
            variant={ButtonVariant.Primary}
            label={t("actions.close")}
            onClick={() => {
              navigate("/minside");
            }}
            icon={<CloseIcon />}
          />
        }
      />
    </div>
  );
};

export default MittJaktomrade;
